// CheckInButton - Toggle check-in at a place

import React, { useState, useEffect } from 'react';
import { MapPin, LogOut, Loader2, Clock, MessageSquare } from 'lucide-react';
import {
  checkIn,
  checkOut,
  subscribeToUserCheckin,
  formatTimeRemaining,
  type CheckIn,
} from '../../services/checkinService';

interface CheckInButtonProps {
  placeId: string;
  placeName: string;
  userId?: string;
  userName?: string;
  userPhoto?: string;
  onShowNotification: (message: string, type: 'success' | 'error' | 'info') => void;
  onAuthRequired?: () => void;
}

export function CheckInButton({
  placeId,
  placeName,
  userId,
  userName,
  userPhoto,
  onShowNotification,
  onAuthRequired,
}: CheckInButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [userCheckin, setUserCheckin] = useState<CheckIn | null>(null);
  const [showMessageInput, setShowMessageInput] = useState(false);
  const [message, setMessage] = useState('');
  const [timeRemaining, setTimeRemaining] = useState<string | null>(null);

  // Subscribe to user's check-in status
  useEffect(() => {
    if (!userId) {
      setUserCheckin(null);
      return;
    }

    const unsubscribe = subscribeToUserCheckin(userId, (checkin) => {
      setUserCheckin(checkin);
    });

    return () => unsubscribe();
  }, [userId]);

  // Update time remaining every minute
  useEffect(() => {
    if (!userCheckin || userCheckin.placeId !== placeId) {
      setTimeRemaining(null);
      return;
    }

    const updateTime = () => {
      setTimeRemaining(formatTimeRemaining(userCheckin.expiresAt));
    };

    updateTime();
    const interval = setInterval(updateTime, 60000);

    return () => clearInterval(interval);
  }, [userCheckin, placeId]);

  const isCheckedInHere = userCheckin?.placeId === placeId;
  const isCheckedInElsewhere = userCheckin && userCheckin.placeId !== placeId;

  const handleCheckIn = async () => {
    if (!userId) {
      onAuthRequired?.();
      return;
    }

    // Use display name or fallback to "Anonymer Nutzer"
    const displayName = userName || 'Anonymer Nutzer';

    setIsLoading(true);
    try {
      await checkIn(placeId, userId, displayName, userPhoto, message || undefined);
      onShowNotification(`Du bist jetzt bei "${placeName}" eingecheckt`, 'success');
      setShowMessageInput(false);
      setMessage('');
    } catch (error) {
      console.error('Check-in failed:', error);
      onShowNotification('Check-in fehlgeschlagen', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!userCheckin) return;

    setIsLoading(true);
    try {
      await checkOut(userCheckin.id);
      onShowNotification('Du hast ausgecheckt', 'success');
    } catch (error) {
      console.error('Check-out failed:', error);
      onShowNotification('Auschecken fehlgeschlagen', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Checked in at this place - show check-out button
  if (isCheckedInHere) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-full">
              <MapPin className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-green-800">Du bist hier!</p>
              {timeRemaining && (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {timeRemaining}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleCheckOut}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 text-green-700 font-medium hover:bg-green-100 rounded-lg transition disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <LogOut className="w-4 h-4" />
            )}
            <span>Auschecken</span>
          </button>
        </div>
        {userCheckin.message && (
          <p className="text-sm text-gray-600 italic pl-4 border-l-2 border-green-200">
            "{userCheckin.message}"
          </p>
        )}
      </div>
    );
  }

  // Checked in elsewhere - show info
  if (isCheckedInElsewhere) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-yellow-100 rounded-full">
            <MapPin className="w-5 h-5 text-yellow-600" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-yellow-800">Du bist woanders eingecheckt</p>
            <p className="text-sm text-yellow-600">
              Checke erst dort aus, um hier einzuchecken.
            </p>
          </div>
          <button
            onClick={handleCheckOut}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 text-yellow-700 font-medium hover:bg-yellow-100 rounded-lg transition disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <LogOut className="w-4 h-4" />
            )}
            <span>Auschecken</span>
          </button>
        </div>
      </div>
    );
  }

  // Not checked in - show check-in button
  return (
    <div className="space-y-3">
      {!showMessageInput ? (
        <button
          onClick={() => setShowMessageInput(true)}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-indigo-500 text-white font-semibold rounded-xl hover:bg-indigo-600 transition disabled:opacity-50"
        >
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <MapPin className="w-5 h-5" />
          )}
          <span>Ich bin hier!</span>
        </button>
      ) : (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
          <div className="flex items-center gap-2 text-gray-700">
            <MessageSquare className="w-4 h-4" />
            <span className="text-sm font-medium">Nachricht hinzufuegen (optional)</span>
          </div>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="z.B. Bin am Fensterplatz"
            maxLength={100}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          />
          <div className="flex gap-2">
            <button
              onClick={() => {
                setShowMessageInput(false);
                setMessage('');
              }}
              className="flex-1 px-4 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition"
            >
              Abbrechen
            </button>
            <button
              onClick={handleCheckIn}
              disabled={isLoading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-indigo-500 text-white font-medium rounded-lg hover:bg-indigo-600 transition disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <MapPin className="w-4 h-4" />
              )}
              <span>Einchecken</span>
            </button>
          </div>
        </div>
      )}
      <p className="text-xs text-gray-500 text-center">
        Dein Check-in ist 3 Stunden sichtbar
      </p>
    </div>
  );
}

export default CheckInButton;
